import { authService } from '@/services/auth-service';

/**
 * Utility function to handle OAuth callback data from URL parameters
 * Can be used on any page that might receive OAuth redirect data
 */
export function handleOAuthCallbackData(searchParams: URLSearchParams): {
  success: boolean;
  error?: string;
} {
  try {
    // Check if this is a successful OAuth callback
    const success = searchParams.get('success');
    const data = searchParams.get('data');
    
    if (success === 'true' && data) {
      try {
        // Decode the base64 encoded auth data from the backend
        const decodedData = atob(data);
        const authData = JSON.parse(decodedData);
        
        // Store the session using the auth service
        authService.storeSession(authData);
        
        // Also store in the legacy localStorage format for immediate AuthProvider access
        if (authData.user) {
          localStorage.setItem('ace-auth', JSON.stringify(authData.user));
        }
        
        // Clean the URL by removing the OAuth parameters
        const url = new URL(window.location.href);
        url.searchParams.delete('success');
        url.searchParams.delete('data');
        url.searchParams.delete('error');
        
        // Replace the current URL without the OAuth parameters
        window.history.replaceState({}, '', url.toString());
        
        // Trigger multiple events to ensure AuthProvider picks up the change
        // 1. Storage event
        window.dispatchEvent(new StorageEvent('storage', {
          key: 'ace-auth-session',
          newValue: JSON.stringify(authData),
          storageArea: localStorage
        }));
        
        // 2. Custom event for immediate notification
        window.dispatchEvent(new CustomEvent('auth-session-updated', {
          detail: { authData, user: authData.user }
        }));
        
        // 3. Force a small delay to ensure AuthProvider re-reads state
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('force-auth-refresh'));
          // Also trigger profile fetch event
          window.dispatchEvent(new CustomEvent('auth-login-success', {
            detail: { user: authData.user }
          }));
        }, 100);
        
        return { success: true };
      } catch (decodeError) {
        return { success: false, error: 'Failed to process authentication data' };
      }
    }
    
    // Check for error callback
    const error = searchParams.get('error');
    if (error) {
      return { success: false, error: decodeURIComponent(error) };
    }
    
    // No OAuth data found
    return { success: false };
  } catch (err: any) {
    return { success: false, error: err.message || 'Authentication processing failed' };
  }
}
